import GyenoMark from './GyenoMark';
import { Bot, ListChecks, MessageSquare, Zap, GitBranch, ShieldAlert, AlertCircle, Activity, Plus, Search, Play } from 'lucide-react';

const navSections = [
  { label: 'BUILD', items: [{ icon: Bot, name: 'Agents', active: true }, { icon: ListChecks, name: 'Action Registry' }] },
  { label: 'MONITOR', items: [{ icon: MessageSquare, name: 'Chat Sessions' }, { icon: Zap, name: 'Action Runs' }, { icon: GitBranch, name: 'Orphan Actions' }, { icon: ShieldAlert, name: 'Policy Violations' }, { icon: AlertCircle, name: 'Error Logs' }] },
  { label: 'INSIGHTS', items: [{ icon: Activity, name: 'Health' }] },
];

function SideNav() {
  return (
    <div className="hidden sm:block w-44 shrink-0 border-r border-[#10282C]/8 bg-white py-3 px-2 text-left">
      <div className="flex items-center gap-2 px-2 pb-3">
        <GyenoMark size={18} interactive={false} />
        <span className="text-[11px] font-semibold text-[#10282C]">Gyeno Studio</span>
      </div>
      {navSections.map((s) => (
        <div key={s.label} className="mb-2">
          <p className="px-2 py-1 text-[8px] font-mono tracking-[0.14em] text-[#10282C]/40">{s.label}</p>
          {s.items.map((it) => (
            <div key={it.name} className={`flex items-center gap-1.5 px-2 py-[5px] rounded-md text-[10px] ${it.active ? 'bg-[#00918F]/10 text-[#00918F] font-semibold' : 'text-[#10282C]/60'}`}>
              <it.icon size={10} strokeWidth={2.2} />
              {it.name}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function StudioHomeMock() {
  return (
    <div className="flex bg-[#F6F8F7] min-h-[340px] text-left">
      <SideNav />
      <div className="flex-1 p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <GyenoMark size={34} interactive={false} />
            <div>
              <h4 className="text-base font-bold text-[#10282C] leading-tight">Gyeno Studio</h4>
              <p className="text-[11px] text-[#10282C]/55">Design, test, and ship AI agents on Salesforce.</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#00918F] text-white text-[10px] font-semibold">
            <Plus size={11} /> New Agent
          </span>
        </div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl bg-white border border-[#10282C]/8 p-4 shadow-sm">
            <span className="inline-block px-2 py-0.5 rounded bg-[#10282C]/6 text-[8px] font-mono tracking-widest text-[#10282C]/60">DRAFT</span>
            <h5 className="mt-2 text-[13px] font-bold text-[#10282C]">Sales Coach</h5>
            <p className="mt-1 text-[10px] leading-relaxed text-[#10282C]/55">Coaches sales reps on their pipeline: what is open, what is slipping, what to do next — and then logs the follow-up.</p>
            <div className="mt-3 flex items-center justify-between text-[9px] text-[#10282C]/45">
              <span className="flex items-center gap-1 font-mono"><span className="w-1.5 h-1.5 rounded-full bg-[#00918F]" /> gemini-2.5-flash</span>
              <span>12h ago</span>
            </div>
          </div>
          <div className="rounded-xl bg-white border border-[#10282C]/8 p-4 shadow-sm">
            <span className="inline-block px-2 py-0.5 rounded bg-[#00C5C2]/15 text-[8px] font-mono tracking-widest text-[#00918F]">LIVE · v3</span>
            <h5 className="mt-2 text-[13px] font-bold text-[#10282C]">Support Triage</h5>
            <p className="mt-1 text-[10px] leading-relaxed text-[#10282C]/55">Reads incoming cases, pulls account history, and routes with a suggested first response.</p>
            <div className="mt-3 flex items-center justify-between text-[9px] text-[#10282C]/45">
              <span className="flex items-center gap-1 font-mono"><span className="w-1.5 h-1.5 rounded-full bg-[#00918F]" /> claude-sonnet</span>
              <span>2d ago</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionCard({ type, name, desc }) {
  return (
    <div className="rounded-lg bg-white border border-[#10282C]/8 px-3 py-2 shadow-sm">
      <div className="flex items-center gap-2">
        <span className={`px-1.5 py-0.5 rounded text-[7px] font-mono font-bold tracking-wider ${type === 'APEX' ? 'bg-[#10282C]/8 text-[#10282C]/70' : 'bg-[#f5c04e]/20 text-[#946b00]'}`}>{type}</span>
        <span className="text-[10px] font-bold text-[#10282C]">{name}</span>
      </div>
      <p className="mt-1 text-[9px] text-[#10282C]/50 leading-snug">{desc}</p>
    </div>
  );
}

export function BuilderCanvasMock() {
  return (
    <div className="flex bg-[#F6F8F7] min-h-[340px] text-left">
      <div className="hidden md:block w-40 shrink-0 border-r border-[#10282C]/8 bg-white p-3">
        <p className="text-[9px] font-mono tracking-[0.14em] text-[#10282C]/40">ACTION LIBRARY</p>
        <div className="mt-2 flex items-center gap-1.5 rounded-md border border-[#10282C]/10 px-2 py-1.5 text-[9px] text-[#10282C]/40">
          <Search size={9} /> Search actions
        </div>
        <div className="mt-3 space-y-2">
          <ActionCard type="APEX" name="FindStalledDeals" desc="Find open deals with no recent activity." />
          <ActionCard type="APEX" name="GetAccountHistory" desc="Won, lost and open deals for an account." />
          <ActionCard type="FLOW" name="LogTask" desc="Create a follow-up task for the rep." />
        </div>
      </div>
      <div className="flex-1 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h5 className="text-[13px] font-bold text-[#10282C]">Sales Coach</h5>
            <span className="px-1.5 py-0.5 rounded bg-[#10282C]/6 text-[7px] font-mono tracking-widest text-[#10282C]/50">DRAFT</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="px-2.5 py-1 rounded-md border border-[#10282C]/12 text-[9px] font-semibold text-[#10282C]/70 flex items-center gap-1"><Play size={8} /> Test</span>
            <span className="px-2.5 py-1 rounded-md bg-[#00918F] text-white text-[9px] font-semibold">Publish</span>
          </div>
        </div>
        <div className="rounded-xl border border-[#10282C]/8 bg-white/70 p-3">
          <p className="text-[9px] font-mono tracking-wider text-[#00918F] font-bold">STAGE 1 · Pipeline Review</p>
          <div className="mt-2 space-y-2">
            <ActionCard type="APEX" name="GetPipeline" desc="Summarise open opportunities: count, total value, breakdown by stage." />
            <ActionCard type="APEX" name="FindStalledDeals" desc="Find open deals with no recent activity, no next step, or a past close date." />
          </div>
        </div>
        <div className="rounded-xl border border-[#10282C]/8 bg-white/70 p-3">
          <p className="text-[9px] font-mono tracking-wider text-[#00918F] font-bold">STAGE 2 · Deal Strategy</p>
          <div className="mt-2">
            <ActionCard type="APEX" name="GetDealDetail" desc="Full picture of one opportunity: stage, amount, close date, next step." />
          </div>
        </div>
        <div className="rounded-lg border border-dashed border-[#10282C]/20 py-2 text-center text-[9px] text-[#10282C]/40">+ Add stage</div>
      </div>
    </div>
  );
}

export function ConfigPanelMock() {
  return (
    <div className="bg-white p-4 text-left min-h-[300px]">
      <p className="text-[9px] font-mono tracking-[0.14em] text-[#10282C]/40">AGENT · CONFIGURATION</p>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <p className="text-[9px] font-semibold text-[#10282C]/70">LLM Model</p>
          <div className="mt-1 rounded-md border border-[#10282C]/12 px-2.5 py-1.5 text-[10px] font-medium text-[#10282C] flex items-center justify-between">
            Gemini 2.5 Flash <span className="text-[#10282C]/30">▾</span>
          </div>
          <div className="mt-1.5 space-y-1">
            {['Claude Sonnet', 'GPT-5', 'Gemini 2.5 Flash'].map((m, i) => (
              <div key={m} className={`px-2.5 py-1 rounded text-[9px] ${i === 2 ? 'bg-[#00C5C2]/12 text-[#00918F] font-semibold' : 'text-[#10282C]/50'}`}>{m}</div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[9px] font-semibold text-[#10282C]/70">Max ReAct steps</p>
          <div className="mt-1 rounded-md border border-[#10282C]/12 px-2.5 py-1.5 text-[10px] font-medium text-[#10282C]">8</div>
          <p className="mt-3 text-[9px] font-mono tracking-[0.14em] text-[#10282C]/40">CHANNELS</p>
          <div className="mt-1.5 space-y-1.5">
            <div className="flex items-center justify-between rounded-lg border border-[#10282C]/8 px-2.5 py-2">
              <span className="text-[9px] font-semibold text-[#10282C]">WhatsApp</span>
              <span className="w-7 h-4 rounded-full bg-[#00918F] relative"><span className="absolute right-0.5 top-0.5 w-3 h-3 rounded-full bg-white" /></span>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-[#10282C]/8 px-2.5 py-2">
              <span className="text-[9px] font-semibold text-[#10282C]">Guest users</span>
              <span className="w-7 h-4 rounded-full bg-[#10282C]/15 relative"><span className="absolute left-0.5 top-0.5 w-3 h-3 rounded-full bg-white" /></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TestConsoleMock({ thinking = true }) {
  return (
    <div className="bg-white flex flex-col min-h-[300px] text-left">
      <div className="px-4 py-2.5 border-b border-[#10282C]/8 flex items-center justify-between">
        <p className="text-[10px] font-bold text-[#10282C]">Test Console</p>
        <span className="text-[8px] font-mono text-[#00918F] bg-[#00C5C2]/12 px-1.5 py-0.5 rounded">DRAFT v1</span>
      </div>
      <div className="flex-1 p-3 space-y-2.5">
        <div className="max-w-[85%] rounded-xl rounded-tl-sm bg-[#F0F4F3] px-3 py-2 text-[10px] text-[#10282C]/80 leading-relaxed">
          Hi! I can review your pipeline, dig into a specific deal, or help you plan your next move. What would you like to look at?
        </div>
        <div className="max-w-[85%] ml-auto rounded-xl rounded-tr-sm bg-[#00918F] px-3 py-2 text-[10px] text-white leading-relaxed">
          Which of my deals are stalling this quarter?
        </div>
        {thinking && (
          <div className="flex items-center gap-2 px-1">
            <GyenoMark size={18} thinking interactive={false} />
            <span className="text-[10px] text-[#10282C]/50">Thinking…</span>
          </div>
        )}
      </div>
      <div className="p-3 border-t border-[#10282C]/8">
        <div className="rounded-lg border border-[#10282C]/12 px-3 py-2 text-[10px] text-[#10282C]/35">Message your agent…</div>
      </div>
    </div>
  );
}

export function PublishModalMock() {
  return (
    <div className="bg-[#10282C]/40 p-6 flex items-center justify-center min-h-[260px]">
      <div className="w-full max-w-xs rounded-xl bg-white p-4 shadow-2xl text-left">
        <h5 className="text-[12px] font-bold text-[#10282C]">Publish new version</h5>
        <div className="mt-2.5 rounded-lg bg-[#00C5C2]/10 px-3 py-2 text-[10px]">
          <span className="font-bold text-[#00918F]">Version 1</span>
          <span className="text-[#10282C]/60"> will be live for this agent.</span>
        </div>
        <p className="mt-2.5 text-[9px] font-semibold text-[#10282C]/70">Release notes (optional)</p>
        <div className="mt-1 h-14 rounded-lg border border-[#10282C]/12 px-2.5 py-2 text-[9px] text-[#10282C]/35">What changed in this version?</div>
        <div className="mt-3 flex justify-end gap-2">
          <span className="px-3 py-1.5 rounded-md border border-[#10282C]/12 text-[9px] font-semibold text-[#10282C]/70">Cancel</span>
          <span className="px-3 py-1.5 rounded-md bg-[#00918F] text-white text-[9px] font-semibold">Publish v1</span>
        </div>
      </div>
    </div>
  );
}
